import { Body1, borderRadius, fontSizes, muiInputFormsCSS, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Divider, TextField } from "@mui/material";
import { CalendarPicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import enGb from "date-fns/locale/en-GB";
import React, { ChangeEvent, useState } from "react";

export interface CalendarWithInputsProps {
    date: Date;
    testId?: string;
    className?: string;
    onDateChange: (date: Date) => void;
}

export const CalendarWithInputComponent = ({ date, className, testId, onDateChange }: CalendarWithInputsProps) => {
    const translate = useIventisTranslate();

    const [localInputDate, setLocalInputDate] = useState(format(utcToZonedTime(date, "UTC"), "yyyy-MM-dd"));

    const handleCalendarInputChange = (updatedDate: Date) => {
        const utcUpdatedDate = zonedTimeToUtc(updatedDate, "UTC");
        onDateChange(utcUpdatedDate);
        setLocalInputDate(format(utcUpdatedDate, "yyyy-MM-dd"));
    };

    const handleDateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const updatedDate = event.target.value;
        setLocalInputDate(updatedDate);
        // If empty string then the date is not valid
        if (updatedDate !== "") {
            onDateChange(new Date(updatedDate));
        }
    };

    return (
        <CalendarWithInputsContainer className={className}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGb}>
                {/** Need to convert back to UTC as output from calendar is in timezone */}
                <CalendarPicker date={utcToZonedTime(date, "UTC")} onChange={handleCalendarInputChange} showDaysOutsideCurrentMonth disableHighlightToday />
            </LocalizationProvider>
            <Divider />
            <div className="input-container">
                <Body1>{translate(Content.map8.dates.startDate)}</Body1>
                <TextField data-testid={`${testId}-input`} className="date-input" type="date" onChange={handleDateInputChange} value={localInputDate} />
            </div>
        </CalendarWithInputsContainer>
    );
};

const CalendarWithInputsContainer = styled.div`
    ${muiInputFormsCSS}
    width: 330px;
    border-radius: ${borderRadius.standard};
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.3);
    .MuiPickersDay-dayOutsideMonth {
        color: ${(props) => props.theme.shades.three};
    }

    .MuiPickersDay-root {
        font-size: ${fontSizes.small};
    }

    .MuiDayPicker-weekDayLabel {
        font-size: ${fontSizes.small};
    }

    .input-container {
        padding: 15px;
        width: 40%;
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .date-input {
        // Hide the calendar picker icon
        input[type="date"]::-webkit-calendar-picker-indicator {
            display: none;
            -webkit-appearance: none;
        }
    }
`;
